import { config } from "@config/config";
import { datadogRum } from "@datadog/browser-rum";
import { CheckBadgeIcon, ExclamationCircleIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import { FeatureFlags } from "@utils/FeatureFlags/generated-flags";
import { EHR_SYNC_OUTBOUND_GATED_MESSAGE } from "@utils/ehrActions";
import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Button, Dialog, Headline, Label, Paragraph, Small } from "@fronterahealth/frontera-ui-components";

import {
  ApiReportEhrSyncChoices,
  useReportEhrStatusQuery,
  useStartProgrammingReviewGoalsSyncToEhrMutation,
} from "@api/graphql/types-and-hooks";
import { Loader } from "@components/ClinicalNewReportDialog/Loader";
import { EhrSyncGatedNotice } from "@components/EHRConnect/EhrSyncGatedNotice";
import { convertReadableString } from "@components/forms/utils";
import { useAdminData } from "@providers/AdminDataProvider";

interface SyncToEHRDialogProps {
  dialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
  disabled: boolean;
  goalsCount: number;
  targetsCount: number;
  assessmentReportId: string;
  onSyncSuccess?: () => void;
}

export const SyncToEHRDialog: React.FC<SyncToEHRDialogProps> = ({
  dialogOpen,
  setDialogOpen,
  disabled,
  goalsCount,
  targetsCount,
  assessmentReportId,
  onSyncSuccess,
}) => {
  const flags = useFlags<FeatureFlags>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { clientId } = useParams();

  const { organizationType, isOrgUsernamePasswordOrg, isEhrSyncCleared } = useAdminData();
  const ehrName = convertReadableString(organizationType);
  const [isSyncingDialogOpen, setIsSyncingDialogOpen] = useState<boolean>(false);
  const [isSyncStarted, setIsSyncStarted] = useState<boolean>(false);
  const [isSyncTimedOut, setIsSyncTimedOut] = useState<boolean>(false);
  const syncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: ehrStatusData } = useReportEhrStatusQuery(
    { reportId: assessmentReportId },
    {
      enabled: !!assessmentReportId && flags["ia-to-programming"] && isSyncStarted,
      queryKey: [assessmentReportId],
      retry: false,
      refetchInterval: (query) => {
        const ehrStatus = query?.state?.data?.getReport?.edges?.[0]?.node?.ehrSync;
        if (ehrStatus === ApiReportEhrSyncChoices.Completed || ehrStatus === ApiReportEhrSyncChoices.Failure) {
          return false;
        }
        return config.REPORT_POLLING_INTERVAL;
      },
    },
  );

  // Derive sync status directly from query data (only valid when sync is started)
  const ehrStatus = ehrStatusData?.getReport?.edges?.[0]?.node?.ehrSync;
  const isSyncingSuccess = isSyncStarted && ehrStatus === ApiReportEhrSyncChoices.Completed;
  const isSyncingError = isSyncStarted && (ehrStatus === ApiReportEhrSyncChoices.Failure || isSyncTimedOut);
  // Show in-progress if sync started and status is pending/inprogress OR if status is not yet available (loading)
  const isSyncingInProgress =
    isSyncStarted &&
    !isSyncingSuccess &&
    !isSyncingError &&
    (ehrStatus === ApiReportEhrSyncChoices.Inprogress ||
      ehrStatus === ApiReportEhrSyncChoices.Pending ||
      ehrStatus === undefined);

  // Cancel the 10-minute stuck-sync timeout once a sync started in this session
  // resolves. Gate on isSyncStarted so a prior session's leftover Failure/Completed
  // status (present on mount) doesn't trigger the connection refetch below.
  useEffect(() => {
    if (isSyncingSuccess || (isSyncStarted && ehrStatus === ApiReportEhrSyncChoices.Failure)) {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
        syncTimeoutRef.current = null;
      }
      setIsSyncTimedOut(false);
      // Org-level admin account: this flow polls its own sync status, not
      // ehr_connection, so refresh the connection when the sync resolves so a
      // credential failure surfaces on the top badge without a manual refresh.
      if (isOrgUsernamePasswordOrg) {
        queryClient.invalidateQueries({ queryKey: ["ehrConnection"] });
      }
    }
  }, [isSyncingSuccess, isSyncStarted, ehrStatus, isOrgUsernamePasswordOrg, queryClient]);

  // Clear any pending timeout on unmount.
  useEffect(() => {
    return () => {
      if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
    };
  }, []);

  const { mutateAsync: startEhrSync } = useStartProgrammingReviewGoalsSyncToEhrMutation({});
  const syncGoalsToEhr = async () => {
    // Managed-onboarding gate: an uncleared tenant can't sync (the backend
    // rejects it too). The button is disabled; guarded here so the "Retry Sync"
    // path is covered as well.
    if (!isEhrSyncCleared) return;
    // Cancel any pending timeout before the new attempt so the old timer can't
    // fire mid-flight. The fresh timer is armed only after the mutation succeeds,
    // ensuring isSyncTimedOut is never reset while the mutation is still in-flight
    // (which would cause the dialog to flip back to in-progress if the call fails).
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
      syncTimeoutRef.current = null;
    }
    startEhrSync({ reportId: assessmentReportId })
      .then(async (data) => {
        if (data?.startProgrammingReviewGoalsSyncToEhr?.success) {
          // Reset the query cache only after mutation succeeds to avoid blank state
          queryClient.removeQueries({ queryKey: [assessmentReportId] });
          setIsSyncTimedOut(false);
          setIsSyncStarted(true);
          setIsSyncingDialogOpen(true);
          // Arm here (not in an effect) so the timer is re-armed on every retry,
          // even when isSyncStarted is already true from a previous attempt.
          syncTimeoutRef.current = setTimeout(() => setIsSyncTimedOut(true), 10 * 60 * 1000);
        }
      })
      .catch((error) => {
        datadogRum.addError(error, { context: `Error Starting ${ehrName} Sync` });
      })
      .finally(() => {
        setDialogOpen(false);
      });
  };

  const renderDialogContent = () => (
    <div className="flex flex-col gap-8">
      {/* Managed-onboarding gate: inline copy next to the disabled Sync control
          (never tooltip-only — a disabled button emits no hover/focus events). */}
      {!isEhrSyncCleared && <EhrSyncGatedNotice message={EHR_SYNC_OUTBOUND_GATED_MESSAGE} />}
      <Paragraph displayType="normal" colorType="primary">
        This sync will commit programming to your EHR and lock edits in Frontera. After syncing, your client's
        programming in Frontera becomes{" "}
        <strong>
          <b>
            <u>read-only.</u>
          </b>
        </strong>{" "}
        Any future changes must be made in your EHR in order to be reflected in Frontera.
        <br />
        <br />
        What we sync to your EHR:
        <ul className="list-disc list-inside">
          <li>Goals and goal metadata</li>
          <li>Teaching Protocol</li>
          <li>Targets and target metadata (if applicable)</li>
        </ul>
      </Paragraph>
      <div className="flex flex-col gap-2 px-6 py-4 bg-limestone-50 rounded-lg">
        <Paragraph displayType="loud" colorType="primary">
          Syncing over to your EHR:
        </Paragraph>
        <Small displayType="normal" colorType="secondary">
          {goalsCount} goals
        </Small>
        <Small displayType="normal" colorType="secondary">
          {targetsCount} targets
        </Small>
      </div>
    </div>
  );

  const renderInProgress = () => (
    <>
      <div className="flex flex-row gap-2 mt-4 items-center">
        <Loader className="h-5 w-5 ml-0 mr-0" />
        <Paragraph colorType="secondary">Syncing in progress...</Paragraph>
      </div>
      <div className="flex flex-col gap-2 p-2 bg-yellow-100 rounded-[4px]">
        <div className="flex flex-row gap-2 items-start">
          <ExclamationCircleIcon className="w-6 h-6 text-yellow-800" />
          <Label size="small" className="text-yellow-800">
            Do not close out of this browser. This will take a few moments.
          </Label>
        </div>
      </div>
    </>
  );

  const renderError = () => (
    <>
      <div className="flex flex-row gap-4 mt-4 items-center">
        <Paragraph>Sync failed</Paragraph>
        <Button
          text="Retry Sync"
          className="px-0! py-0! h-auto!"
          appearance="text"
          onClick={() => {
            // Don't reset isSyncStarted here - keep showing error until mutation succeeds
            syncGoalsToEhr();
          }}
        />
      </div>
      <div className="flex flex-col gap-2 p-2 bg-red-100 rounded-[4px]">
        <div className="flex flex-row gap-2 items-start">
          <ExclamationTriangleIcon className="w-6 h-6 text-red-800" />
          <Label size="small" className="text-red-800">
            We weren't able to sync your programming to the EHR. Nothing was updated, and your programming is still safe
            here. Please try again.
          </Label>
        </div>
      </div>
    </>
  );

  const renderSuccess = () => (
    <>
      <div className="flex flex-col gap-4 items-center justify-center">
        <CheckBadgeIcon className="w-12 h-12 text-green-700" />
        <Headline size="small">Programming Sync is complete!</Headline>
        <Paragraph className="text-center">
          Your initial assessment programming data has been successfully synced to your EHR. Please use your EHR for any
          future edits or changes.
        </Paragraph>
      </div>
      <div className="flex flex-col gap-4 pt-4 items-center justify-center">
        <Button
          text="Go to Programming"
          appearance="primary"
          onClick={() => {
            setIsSyncingDialogOpen(false);
            setDialogOpen(false);
            onSyncSuccess?.();
            navigate(`/clients/${clientId}/programming`);
          }}
        />
      </div>
    </>
  );

  const renderSyncingDialogContent = () => {
    if (isSyncingSuccess) return renderSuccess();
    if (isSyncingError) return renderError();
    if (isSyncingInProgress || isSyncStarted) return renderInProgress();
    return null;
  };

  return (
    <>
      <Dialog
        size="xl"
        open={dialogOpen}
        setOpen={setDialogOpen}
        title={`Sync Programming to ${ehrName || "EHR"}`}
        primaryButton={{
          text: "Sync to EHR",
          onClick: async () => {
            await syncGoalsToEhr();
          },
          disabled: disabled || !isEhrSyncCleared,
        }}
        secondaryButton={{
          text: "Cancel",
          onClick: () => {
            setDialogOpen(false);
          },
          className:
            "ring-0! ring-transparent! focus:ring-0! focus-visible:ring-0! hover:ring-0! shadow-none! hover:bg-transparent! ",
        }}
      >
        {renderDialogContent()}
      </Dialog>
      <Dialog
        size="xl"
        open={isSyncingDialogOpen}
        setOpen={(open) => {
          if (isSyncingInProgress) {
            return;
          }
          setIsSyncingDialogOpen(open);
          setIsSyncStarted(false);
        }}
        title={isSyncingSuccess ? "" : `Syncing to ${ehrName || "EHR"}`}
        primaryButton={undefined}
        secondaryButton={undefined}
        showCloseButton={false}
      >
        <div className="flex flex-col gap-4 w-full pb-12">{renderSyncingDialogContent()}</div>
      </Dialog>
    </>
  );
};
